import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './GuideMedia.module.css';

const PUB = process.env.PUBLIC_URL || '';

/**
 * A short, silent, looping clip for one guide feature, shown directly under its summary.
 *
 * Files live in `public/guide/` and are referenced by the feature id from guideData.js:
 *
 *   public/guide/<id>.webm   VP9, what most browsers download
 *   public/guide/<id>.mp4    H.264, fallback for older Safari / iOS
 *   public/guide/<id>.webp   poster still
 *
 * See HELP-CENTER-CLIPS.md for how these are recorded and encoded.
 *
 * These are videos rather than GIFs on purpose: the same 15 seconds of UI is 8-25 MB as a
 * GIF and 150-350 KB as a silent WebM, and a muted looping video with no controls is
 * indistinguishable from a GIF on screen.
 *
 * Nothing is fetched until the reader scrolls near the clip, and only the clip actually on
 * screen plays. Ten features would otherwise cost several MB and ten simultaneous decode
 * loops on page load. The upshot is that the clip someone is reading loops continuously
 * while the rest stay idle.
 *
 * Existence is probed via the poster: if `<id>.webp` loads, the clip set is present and the
 * video renders. If it 404s the whole figure collapses to nothing, so a feature whose clip
 * has not been recorded yet looks exactly as it did before and adding one later needs no
 * code change.
 */

// Decided once per mount. A viewer on Save Data, a 2G connection, or with reduced motion
// turned on gets the poster still plus a play button instead of autoplay, so they pay for
// the ~25 KB poster and choose whether to spend the rest.
function prefersStill() {
  if (typeof window === 'undefined') return false;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const conn =
    navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const slow = conn?.effectiveType === '2g' || conn?.effectiveType === 'slow-2g';
  return Boolean(reduced || conn?.saveData === true || slow);
}

export default function GuideMedia({ src, alt, compact = false }) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  // The in-flight play() promise, so a pause never interrupts one mid-start.
  const playPromise = useRef(null);

  const [near, setNear] = useState(false);     // close enough to start fetching
  const [inView, setInView] = useState(false); // on screen, so it should be playing
  const [exists, setExists] = useState(false); // poster loaded => files are present
  const [failed, setFailed] = useState(false); // no files for this id => render nothing
  const [still, setStill] = useState(prefersStill);
  const [playing, setPlaying] = useState(false);

  // Two observers because the trigger distances differ: fetch a little early so the clip is
  // ready by the time it is read, but only play once it is genuinely on screen.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setNear(true);
      setInView(true);
      return undefined;
    }

    const nearObs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setNear(true);
          nearObs.disconnect(); // one way: once fetched, stay fetched
        }
      },
      { rootMargin: '300px 0px' },
    );
    const viewObs = new IntersectionObserver(
      ([e]) => setInView(e.isIntersecting),
      { threshold: 0.15 },
    );

    nearObs.observe(el);
    viewObs.observe(el);
    return () => {
      nearObs.disconnect();
      viewObs.disconnect();
    };
  }, []);

  // Probe the poster to find out whether this feature has a clip yet. Using the poster as the
  // existence check keeps the self-hiding behaviour deterministic: a <video> with <source>
  // children does not reliably fire a single error event when every source 404s.
  useEffect(() => {
    if (!near || !src || exists || failed) return undefined;
    const img = new Image();
    img.onload = () => setExists(true);
    img.onerror = () => setFailed(true);
    img.src = `${PUB}/guide/${src}.webp`;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [near, src, exists, failed]);

  // Play only what is on screen; pause everything else.
  //
  // Two things here are load-bearing, and getting either wrong strands a clip behind a play
  // button it never needed:
  //
  //   1. Only NotAllowedError means autoplay was actually refused by policy. Scrolling past a
  //      clip rejects the pending play() with AbortError ("interrupted by a call to pause"),
  //      which is entirely normal. Treating that as a refusal latched `still` on permanently,
  //      so any clip you scrolled past quickly kept a play button forever, even on scroll back.
  //   2. Never pause a play() that has not settled. Awaiting it first is what stops the abort
  //      from being generated in the first place, rather than just forgiving it afterwards.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !exists || still) return undefined;

    let cancelled = false;

    if (inView) {
      v.muted = true; // set on the element too: React's `muted` prop alone is unreliable
      const p = v.play();
      playPromise.current = p;
      if (p?.catch) {
        p.catch((err) => {
          if (cancelled) return;
          if (err?.name === 'NotAllowedError') setStill(true);
          // Anything else is transient. Leave `still` alone: the clip retries the next time
          // it scrolls into view, which is the self-healing path.
        });
      }
    } else {
      const pending = playPromise.current;
      if (pending?.then) {
        pending.then(() => { if (!v.paused) v.pause(); }).catch(() => {});
      } else {
        v.pause();
      }
    }

    return () => { cancelled = true; };
  }, [inView, exists, still]);

  const handleManualPlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => {});
    setStill(false); // they asked for motion, so let it behave like the rest from here
  }, []);

  if (!src || failed) return null;

  const base = `${PUB}/guide/${src}`;
  const cls = [
    styles.frame,
    exists ? styles.ready : styles.pending,
    compact ? styles.compact : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <figure ref={wrapRef} className={cls}>
      {exists && (
        <>
          <video
            ref={videoRef}
            className={styles.clip}
            poster={`${base}.webp`}
            loop
            muted
            playsInline
            preload={still ? 'none' : 'auto'}
            aria-label={alt || undefined}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          >
            <source src={`${base}.webm`} type="video/webm" />
            <source src={`${base}.mp4`} type="video/mp4" />
          </video>

          {still && !playing && (
            <button
              type="button"
              className={styles.play}
              onClick={handleManualPlay}
              aria-label={alt ? `Play ${alt}` : 'Play clip'}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5v14l11-7z" fill="currentColor" />
              </svg>
            </button>
          )}
        </>
      )}
    </figure>
  );
}
